import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { greeting, fmtDate } from "@/lib/helpers";
import { StatusBadge, PriorityBadge } from "@/components/common/StatusBadge";
import { LoadingState, EmptyState } from "@/components/common/EmptyState";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  Briefcase, Gavel, CalendarClock, ListChecks, Users, AlertTriangle, Clock, ArrowRight, NotebookPen, Plus, Bell,
} from "lucide-react";

const STAT_CARDS = [
  { key: "active_cases", label: "Active Cases", icon: Briefcase, to: "/cases" },
  { key: "today_hearings", label: "Today's Hearings", icon: Gavel, to: "/diary" },
  { key: "upcoming_hearings", label: "Upcoming Hearings", icon: CalendarClock, to: "/hearings" },
  { key: "pending_tasks", label: "Pending Tasks", icon: ListChecks, to: "/tasks" },
  { key: "total_clients", label: "Total Clients", icon: Users, to: "/clients" },
  { key: "attention_cases", label: "Need Attention", icon: AlertTriangle, to: "/cases", warn: true },
];

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <LoadingState />;
  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const rem = data.reminders || {};
  const banners = [];
  if (rem.today_hearings > 0) banners.push({ t: `You have ${rem.today_hearings} hearing${rem.today_hearings > 1 ? "s" : ""} scheduled today.`, to: "/diary", tone: "gold" });
  if (rem.tomorrow_hearings > 0) banners.push({ t: `You have ${rem.tomorrow_hearings} hearing${rem.tomorrow_hearings > 1 ? "s" : ""} tomorrow.`, to: "/diary", tone: "navy" });
  if (rem.overdue_tasks > 0) banners.push({ t: `You have ${rem.overdue_tasks} overdue task${rem.overdue_tasks > 1 ? "s" : ""}.`, to: "/tasks", tone: "rose" });
  const toneCls = { gold: "border-gold/40 bg-gold/10 text-navy", navy: "border-navy/20 bg-navy/5 text-navy", rose: "border-rose-200 bg-rose-50 text-rose-700" };

  return (
    <div data-testid="dashboard-page" className="space-y-8">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="font-display text-4xl tracking-tight text-navy">
            {greeting()}, {user?.name?.replace(/^Adv\.\s*/, "")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{today}</p>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="quick-actions">
          {[["Add Case", "/cases"], ["Add Client", "/clients"], ["Add Hearing", "/hearings"], ["Add Task", "/tasks"]].map(([label, to]) => (
            <button key={to} onClick={() => navigate(to)} data-testid={`quick-${label.split(" ")[1].toLowerCase()}`}
              className="flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-2 text-sm font-medium text-navy transition-colors hover:border-gold hover:bg-secondary/50">
              <Plus className="h-3.5 w-3.5 text-gold" />{label}
            </button>
          ))}
        </div>
      </div>

      {banners.length > 0 && (
        <div className="space-y-2" data-testid="reminder-banners">
          {banners.map((b, i) => (
            <button key={i} onClick={() => navigate(b.to)} data-testid={`reminder-${b.tone}`}
              className={`flex w-full items-center gap-2 rounded-md border px-4 py-2.5 text-left text-sm font-medium ${toneCls[b.tone]}`}>
              <Bell className="h-4 w-4" />{b.t}
            </button>
          ))}
        </div>
      )}


      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        {STAT_CARDS.map(({ key, label, icon: Icon, to, warn }) => (
          <button
            key={key}
            onClick={() => navigate(to)}
            data-testid={`stat-${key.replace(/_/g, "-")}`}
            className="group flex flex-col items-start rounded-lg border border-border bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-gold"
          >
            <div className={cn("mb-3 flex h-9 w-9 items-center justify-center rounded-md", warn ? "bg-rose-50 text-rose-600" : "bg-navy/5 text-navy")}>
              <Icon className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <span className="font-display text-3xl leading-none text-navy">{data.stats[key]}</span>
            <span className="mt-1 text-xs text-muted-foreground">{label}</span>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Today's court diary */}
        <Card className="lg:col-span-2 border-border shadow-sm">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div className="flex items-center gap-2">
              <Gavel className="h-4 w-4 text-gold" />
              <h2 className="font-display text-xl text-navy">Today's Court Diary</h2>
            </div>
            <button onClick={() => navigate("/diary")} className="flex items-center gap-1 text-xs font-medium text-navy hover:text-gold">
              Open diary <ArrowRight className="h-3 w-3" />
            </button>
          </div>
          <div className="divide-y divide-border">
            {data.today_diary.length === 0 && (
              <div className="px-5 py-10 text-center text-sm text-muted-foreground">No hearings scheduled for today.</div>
            )}
            {data.today_diary.map((h) => (
              <button
                key={h.id}
                onClick={() => navigate(`/cases/${h.case_id}`)}
                data-testid={`diary-hearing-${h.id}`}
                className="flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-secondary/50"
              >
                <div className="flex w-14 shrink-0 flex-col items-center rounded-md bg-navy/5 py-1.5">
                  <Clock className="h-3.5 w-3.5 text-navy" />
                  <span className="mt-0.5 text-xs font-semibold text-navy">{h.time || "—"}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-navy">{h.case_title}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {h.case_number} · {h.court} · {h.purpose}
                  </p>
                </div>
                <StatusBadge status={h.status} />
              </button>
            ))}
          </div>
        </Card>

        {/* Pending tasks */}
        <Card className="border-border shadow-sm">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div className="flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-gold" />
              <h2 className="font-display text-xl text-navy">Pending Tasks</h2>
            </div>
          </div>
          <div className="divide-y divide-border">
            {data.pending_tasks.length === 0 && <div className="px-5 py-10 text-center text-sm text-muted-foreground">No pending tasks.</div>}
            {data.pending_tasks.slice(0, 6).map((t) => (
              <div key={t.id} className="px-5 py-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-navy">{t.title}</p>
                  <PriorityBadge priority={t.priority} />
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t.case_title ? `${t.case_title} · ` : ""}Due {fmtDate(t.due_date)}
                </p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Upcoming hearings */}
        <Card className="border-border shadow-sm">
          <div className="flex items-center gap-2 border-b border-border px-5 py-4">
            <CalendarClock className="h-4 w-4 text-gold" />
            <h2 className="font-display text-xl text-navy">Upcoming Hearings (7 days)</h2>
          </div>
          <div className="divide-y divide-border">
            {data.upcoming_hearings.length === 0 && <div className="px-5 py-10 text-center text-sm text-muted-foreground">No upcoming hearings.</div>}
            {data.upcoming_hearings.slice(0, 6).map((h) => (
              <button key={h.id} onClick={() => navigate(`/cases/${h.case_id}`)} className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-secondary/50">
                <div className="w-24 shrink-0 text-xs font-medium text-navy">{fmtDate(h.date)}</div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-navy">{h.case_title}</p>
                  <p className="truncate text-xs text-muted-foreground">{h.court} · {h.time}</p>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Recent case activity */}
        <Card className="border-border shadow-sm">
          <div className="flex items-center gap-2 border-b border-border px-5 py-4">
            <NotebookPen className="h-4 w-4 text-gold" />
            <h2 className="font-display text-xl text-navy">Recent Case Activity</h2>
          </div>
          <div className="divide-y divide-border">
            {data.recent_cases.map((c) => (
              <button key={c.id} onClick={() => navigate(`/cases/${c.id}`)} className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-secondary/50">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-navy">{c.title}</p>
                  <p className="truncate text-xs text-muted-foreground">{c.case_number} · Updated {fmtDate(c.updated_at)}</p>
                </div>
                <StatusBadge status={c.status} />
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
